import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiBriefcase, FiTool, FiTruck, FiCheckCircle } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiHome,
      title: 'Residential Cleaning',
      description: 'Complete home cleaning services for your comfort and peace of mind.',
      features: [
        'Regular weekly/bi-weekly/monthly cleaning',
        'Deep cleaning services',
        'Kitchen and bathroom sanitization',
        'Dusting and vacuuming',
        'Floor mopping and care',
        'Trash removal'
      ],
      price: 'Starting at $80',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&h=400&fit=crop'
    },
    {
      icon: FiBriefcase,
      title: 'Commercial Cleaning',
      description: 'Professional cleaning solutions for offices and business facilities.',
      features: [
        'Office space cleaning',
        'Retail store maintenance',
        'Restroom sanitization',
        'Window cleaning',
        'Carpet cleaning',
        'Flexible scheduling'
      ],
      price: 'Custom quotes',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=400&fit=crop'
    },
    {
      icon: FiTool,
      title: 'Deep Cleaning',
      description: 'Thorough, intensive cleaning for a fresh start in any space.',
      features: [
        'Inside appliance cleaning',
        'Baseboards and trim',
        'Light fixture cleaning',
        'Cabinet interior cleaning',
        'Detailed bathroom scrubbing',
        'Window sill cleaning'
      ],
      price: 'Starting at $200',
      image: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=500&h=400&fit=crop'
    },
    {
      icon: FiTruck,
      title: 'Move-In/Move-Out',
      description: 'Complete cleaning services for moving transitions.',
      features: [
        'Empty property cleaning',
        'All rooms and closets',
        'Appliance cleaning',
        'Cabinet and drawer cleaning',
        'Bathroom deep clean',
        'Final walkthrough'
      ],
      price: 'Starting at $250',
      image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=500&h=400&fit=crop'
    }
  ];

  const additionalServices = [
    'Window cleaning (interior/exterior)',
    'Carpet and upholstery cleaning',
    'Post-construction cleanup',
    'Event cleanup services',
    'Garage and basement cleaning',
    'Pressure washing services'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              Our
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {' '}Services
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional cleaning solutions tailored to your specific needs. 
              From regular maintenance to deep cleaning, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="md:flex">
                  <div className="md:w-1/2">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </div>
                  <div className="md:w-1/2 p-8">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-4">
                        <SafeIcon icon={service.icon} className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                    </div>
                    
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-blue-600">{service.price}</span>
                      <Link
                        to="/booking"
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full font-medium hover:shadow-lg transform hover:scale-105 transition-all"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600">
              We offer specialized cleaning services for unique needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500 mr-3" />
                  <span className="text-lg font-medium text-gray-900">{service}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple steps to get your space professionally cleaned
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Book Online',
                description: 'Choose your service and schedule a convenient time through our easy booking system.'
              },
              {
                step: '2',
                title: 'We Clean',
                description: 'Our trained professionals arrive on time with all necessary supplies and equipment.'
              },
              {
                step: '3',
                title: 'Enjoy',
                description: 'Relax and enjoy your sparkling clean space. 100% satisfaction guaranteed.'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Book your cleaning service today and let us take care of the rest
            </p>
            <Link
              to="/booking"
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              Book Your Service Now
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Services;